import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /payment/success?session_id=...
 * Polls /api/payments/status/{session_id} until paid/expired/failed.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");

  const [state, setState] = useState({ status: "checking", data: null, error: null });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session_id in URL" });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired" || data.status === "failed") {
          setState({ status: data.status, data });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setState({ status: "timeout", data });
          return;
        }
        timerRef.current = setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: e?.response?.data?.detail || e.message });
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  const goBack = () => {
    const kind = state.data?.kind;
    const relatedId = state.data?.related_id;
    if (kind === "appointment") {
      navigate("/appointments");
    } else if (kind === "quote_deposit") {
      navigate(`/b/quote/${relatedId}`);
    } else {
      navigate("/");
    }
  };

  const isPaid = state.status === "paid";
  const isError = ["error", "expired", "failed", "timeout"].includes(state.status);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-6"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-10 text-center">
        {state.status === "checking" && (
          <div data-testid="payment-status-checking">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                <path
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  className="opacity-75"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Confirming your payment…</h2>
            <p className="text-sm text-gray-500 mt-2">This usually takes a few seconds.</p>
          </div>
        )}

        {isPaid && (
          <div data-testid="payment-status-paid">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
              <svg className="w-9 h-9 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Payment successful</h2>
            <p className="text-sm text-gray-600 mt-2">
              We've received <strong>${Number(state.data?.amount || 0).toFixed(2)}</strong>. A receipt has been
              emailed to you by Stripe.
            </p>
            <button
              data-testid="payment-back-btn"
              onClick={goBack}
              className="mt-8 inline-flex items-center justify-center px-6 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
            >
              Continue
            </button>
          </div>
        )}

        {isError && (
          <div data-testid="payment-status-error">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {state.status === "timeout" ? "Still processing" : "Payment not completed"}
            </h2>
            <p className="text-sm text-gray-500 mt-2">
              {state.error ||
                (state.status === "timeout"
                  ? "We're still confirming. You'll receive an email once your payment clears."
                  : "Please try again or contact support if this persists.")}
            </p>
            <button
              data-testid="payment-back-btn"
              onClick={goBack}
              className="mt-8 inline-flex items-center justify-center px-6 py-3 rounded-lg bg-gray-900 text-white font-medium hover:bg-gray-800 transition"
            >
              Go back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
